import React, { useState, useEffect } from 'react';
import { IoDocumentTextOutline, IoTimeOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline, IoHourglassOutline, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function BankWithdrawalsContent() {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // API'den veri çekme
    useEffect(() => {
        fetchBankWithdrawals();
    }, []);

    const fetchBankWithdrawals = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await api.getBankWithdrawals();

            if (result.success) {
                setWithdrawals(result.data || []);
            } else {
                throw new Error(result.message || t('bankWithdrawalsContent.dataFetchError'));
            }
        } catch (err) {
            console.error('API Error:', err);
            setError(err.message || t('bankWithdrawalsContent.errorLoadingData'));
            toast.error(t('bankWithdrawalsContent.errorLoadingData'));
        } finally {
            setLoading(false);
        }
    };

    // Tarih formatını düzenleme
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // API'den gelen veriye göre durum belirleme (API'de status yok, hepsini completed olarak göster)
    const getStatusIcon = () => {
        return <IoCheckmarkCircleOutline size={20} className="text-success" />;
    };

    const getStatusText = () => {
        return t('bankWithdrawalsContent.status.completed');
    };

    const getStatusClass = () => {
        return 'status-completed';
    };

    const totalPages = Math.ceil(withdrawals.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = withdrawals.slice(startIndex, endIndex);

    return (
        <div className="crypto-withdrawals-content">
            <div className="change-password-header">
                <h1>{t('bankWithdrawalsContent.title')}</h1>
                <p>{t('bankWithdrawalsContent.subtitle')}</p>
            </div>

            <div className="content-body">
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>{t('bankWithdrawalsContent.loading')}</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p>{t('bankWithdrawalsContent.error')} {error}</p>
                        <button onClick={fetchBankWithdrawals} className="retry-btn">
                            {t('bankWithdrawalsContent.retryButton')}
                        </button>
                    </div>
                ) : (
                    <div className="table-container">
                        {currentData.length === 0 ? (
                            <div className="no-data">
                                <div className="no-data-content">
                                    <IoDocumentTextOutline size={48} />
                                    <p>{t('bankWithdrawalsContent.noDataMessage')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="div-table">
                                {/* Header */}
                                <div className="div-table-header">
                                    <div className="div-table-cell">{t('bankWithdrawalsContent.tableHeaders.accountOwner')}</div>
                                    <div className="div-table-cell">{t('bankWithdrawalsContent.tableHeaders.amount')}</div>
                                    <div className="div-table-cell">{t('bankWithdrawalsContent.tableHeaders.bank')}</div>
                                    <div className="div-table-cell">{t('bankWithdrawalsContent.tableHeaders.iban')}</div>
                                    <div className="div-table-cell">{t('bankWithdrawalsContent.tableHeaders.status')}</div>
                                    <div className="div-table-cell">{t('bankWithdrawalsContent.tableHeaders.date')}</div>
                                </div>

                                {/* Body */}
                                <div className="div-table-body">
                                    {currentData.map((item) => (
                                        <div key={item.withdrawalId} className="div-table-row">
                                            <div className="div-table-cell" data-label={t('bankWithdrawalsContent.tableHeaders.accountOwner')}>
                                                <div className="coin-info">
                                                    <span className="coin-symbol">{item.accountOwner}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('bankWithdrawalsContent.tableHeaders.amount')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.amount}</span>
                                                    <span className="amount">{item.currency}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('bankWithdrawalsContent.tableHeaders.bank')}>
                                                <div className="address-info">
                                                    <span className="address">{item.bankName}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('bankWithdrawalsContent.tableHeaders.iban')}>
                                                <div className="address-info">
                                                    <span className="address">{item.iban}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('bankWithdrawalsContent.tableHeaders.status')}>
                                                <div className={`status-badge ${getStatusClass()}`}>
                                                    <span>{getStatusText()}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('bankWithdrawalsContent.tableHeaders.date')}>
                                                <div className="date-info">
                                                    <span className="date">{formatDate(item.createdDate)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <div className="pagination">
                                    <button
                                        className="pagination-btn prev-btn"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <IoChevronBackOutline size={16} />
                                    </button>

                                    {/* Show current page and next 2 pages */}
                                    {Array.from({ length: Math.min(3, totalPages - currentPage + 1) }, (_, i) => {
                                        const page = currentPage + i;
                                        if (page > totalPages) return null;
                                        return (
                                            <button
                                                key={page}
                                                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button
                                        className="pagination-btn next-btn"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <IoChevronForwardOutline size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
